import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { resolvePrivateImage, formatRM, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Upload, MessageCircle, Loader2, ArrowLeft, CreditCard, CheckCircle2 } from "lucide-react";

export default function BookingDetail() {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const zh = !i18n.language?.startsWith("en");
  const [booking, setBooking] = useState(null);
  const [settings, setSettings] = useState({});
  const [payCfg, setPayCfg] = useState({ enabled: false });
  const [uploading, setUploading] = useState(false);
  const [paying, setPaying] = useState(false);

  const load = () => api.get(`/bookings/${id}`).then((r) => setBooking(r.data)).catch(() => setBooking(null));
  useEffect(() => {
    load();
    api.get("/settings").then((r) => setSettings(r.data));
    api.get("/payments/config").then((r) => setPayCfg(r.data));
    // eslint-disable-next-line
  }, [id]);

  if (!booking) return <div className="p-16 text-[#4a6e77]">{t("common.loading")}</div>;

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/bookings/${booking.id}/proof`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(t("common.saved"));
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const waLink = () => {
    const num = (settings.whatsapp_number || "").replace(/[^\d]/g, "");
    if (!num) return null;
    const msg = zh
      ? `您好，我的预约时段 ${new Date(booking.starts_at).toLocaleString()}，已付定金 ${formatRM(booking.deposit || 0)}。`
      : `Hello, my booking at ${new Date(booking.starts_at).toLocaleString()}, deposit ${formatRM(booking.deposit || 0)} paid.`;
    return `https://wa.me/${num}?text=${encodeURIComponent(msg)}`;
  };

  return (
    <div className="mx-auto max-w-3xl px-6 lg:px-10 py-16" data-testid="booking-detail-page">
      <Link to="/dashboard" className="text-sm text-[#4a6e77] hover:text-[#005870] inline-flex items-center gap-1.5">
        <ArrowLeft size={14} /> {t("common.back")}
      </Link>
      <h1 className="mt-3 font-serif-cn text-3xl text-[#003645] font-semibold">{t("booking.title")}</h1>

      <div className="mt-4 flex items-center gap-3">
        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
          booking.status === "confirmed" ? "bg-[#00868e]/10 text-[#00868e]" :
          booking.status === "rejected" ? "bg-red-50 text-red-700" :
          "bg-amber-50 text-amber-700"
        }`} data-testid="booking-status">
          {t(`booking.statuses.${booking.status}`)}
        </span>
        {waLink() && (
          <a href={waLink()} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-sm text-[#25D366]" data-testid="booking-wa-btn">
            <MessageCircle size={14} /> {t("order.contactWa")}
          </a>
        )}
      </div>

      {booking.status === "rejected" && (
        <div className="mt-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          <div className="font-medium">{t("order.rejectReason")}</div>
          <div className="mt-1">{booking.reject_reason || "-"}</div>
        </div>
      )}

      <div className="mt-6 rounded-lg border border-[#e5ebeb] bg-white p-6">
        <div className="text-xs uppercase tracking-widest text-[#00868e] font-semibold">details</div>
        <div className="mt-3 text-sm text-[#003645] leading-relaxed space-y-1.5">
          <div><b>{new Date(booking.starts_at).toLocaleString()}</b> · {booking.duration_min} {t("booking.minutes")}</div>
          {(booking.master_name_zh || booking.master_name_en) && (
            <div className="text-[#00868e]" data-testid="booking-master-name">
              {zh ? "师傅" : "Master"}: {zh ? booking.master_name_zh : booking.master_name_en}
            </div>
          )}
          <div className="text-[#4a6e77]">{booking.name} · {booking.phone}</div>
          {booking.contact_email && (
            <div className="text-[#4a6e77]" data-testid="booking-contact-email">✉ {booking.contact_email}</div>
          )}
          {booking.birth_snapshot?.birth_year && (
            <div className="text-xs text-[#4a6e77]" data-testid="booking-birth-snapshot">
              {t(`profile.${booking.birth_snapshot.gender || "other"}`)} · {booking.birth_snapshot.birth_year}-{String(booking.birth_snapshot.birth_month).padStart(2,"0")}-{String(booking.birth_snapshot.birth_day).padStart(2,"0")} {String(booking.birth_snapshot.birth_hour).padStart(2,"0")}:00
            </div>
          )}
          {booking.concern && <div className="text-[#4a6e77]">📝 {booking.concern}</div>}
        </div>
      </div>

      <div className="mt-6 rounded-lg border border-[#e5ebeb] bg-white p-6" data-testid="booking-proof-section">
        <div className="text-xs uppercase tracking-widest text-[#00868e] font-semibold">deposit · {formatRM(booking.deposit || 0)}</div>

        {/* Stripe-paid booking: show paid state, hide bank + proof */}
        {booking.payment_method === "stripe" && booking.stripe_payment_status === "paid" ? (
          <div className="mt-4 inline-flex items-center gap-2 text-[#00868e]" data-testid="booking-stripe-paid">
            <CheckCircle2 size={16} /> {zh ? "已通过 Stripe 支付" : "Paid via Stripe"}
          </div>
        ) : (
          <>
            <div className="mt-3 grid sm:grid-cols-3 gap-3 text-sm">
              <div><div className="text-[#4a6e77]">{t("checkout.bankName")}</div><div className="text-[#003645] font-medium">{settings.bank_name}</div></div>
              <div><div className="text-[#4a6e77]">{t("checkout.accountName")}</div><div className="text-[#003645] font-medium">{settings.bank_account_name}</div></div>
              <div><div className="text-[#4a6e77]">{t("checkout.accountNumber")}</div><div className="text-[#003645] font-medium tracking-wider">{settings.bank_account_number}</div></div>
            </div>

            {booking.status !== "confirmed" && payCfg.enabled && (
              <button
                type="button"
                disabled={paying}
                onClick={async () => {
                  setPaying(true);
                  try {
                    const { data } = await api.post("/payments/checkout", {
                      kind: "booking", ref_id: booking.id, origin_url: window.location.origin,
                    });
                    window.location.href = data.checkout_url;
                  } catch (err) { toast.error(formatApiError(err)); setPaying(false); }
                }}
                className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#005870] text-white text-sm font-medium hover:bg-[#003645] disabled:opacity-60"
                data-testid="booking-pay-stripe-btn"
              >
                {paying ? <Loader2 size={14} className="animate-spin" /> : <CreditCard size={14} />}
                {zh ? "在线支付定金" : "Pay deposit online"}
              </button>
            )}
            <div className="mt-5 text-xs text-[#4a6e77]">
              {zh ? "或使用银行转账后上传凭证：" : "Or transfer to the bank details above and upload proof:"}
            </div>

            {booking.payment_proof ? (
              <div className="mt-4">
                <a href={resolvePrivateImage(booking.payment_proof)} target="_blank" rel="noopener noreferrer" data-testid="booking-view-proof">
                  <img src={resolvePrivateImage(booking.payment_proof)} alt="proof" className="max-h-64 rounded border border-[#e5ebeb]" />
                </a>
                {booking.status !== "confirmed" && (
                  <div className="mt-3">
                    <label className="inline-flex items-center gap-2 text-sm text-[#005870] hover:text-[#00868e] cursor-pointer">
                      <Upload size={14} /> {t("order.reuploadProof")}
                      <input type="file" accept="image/*" className="hidden" onChange={onUpload} data-testid="booking-reupload-input" />
                    </label>
                  </div>
                )}
              </div>
            ) : (
              <div className="mt-4">
                <label className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-[#005870] text-[#005870] text-sm font-medium hover:bg-[#005870]/5 cursor-pointer">
                  {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                  {t("order.uploadProof")}
                  <input type="file" accept="image/*" className="hidden" onChange={onUpload} disabled={uploading} data-testid="booking-upload-input" />
                </label>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
